/** Lazy viewport capture. The screenshot library loads only when Send is pressed. */

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.72;

function fitCanvas(source, maxEdge) {
  const edge = Math.max(source.width, source.height);
  if (edge <= maxEdge) return source;
  const scale = maxEdge / edge;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToEncoded(canvas) {
  const fitted = fitCanvas(canvas, MAX_EDGE);
  let dataUrl = "";
  let mime = "image/jpeg";
  try {
    dataUrl = fitted.toDataURL("image/jpeg", JPEG_QUALITY);
  } catch {
    dataUrl = "";
  }
  if (!dataUrl || dataUrl.startsWith("data:image/png")) {
    try {
      const webp = fitted.toDataURL("image/webp", JPEG_QUALITY);
      if (webp.startsWith("data:image/webp")) {
        dataUrl = webp;
        mime = "image/webp";
      }
    } catch {
      /* keep the jpeg or png result */
    }
  }
  const encoded = dataUrl.includes(",") ? dataUrl.split(",")[1] : "";
  return { encoded, mime };
}

function unionRect(rects) {
  const list = (rects || []).filter((rect) => rect && rect.width > 1 && rect.height > 1);
  if (!list.length) return null;
  const left = Math.min(...list.map((rect) => rect.x));
  const top = Math.min(...list.map((rect) => rect.y));
  const right = Math.max(...list.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...list.map((rect) => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function cropCanvas(source, rect, scaleX, scaleY) {
  const pad = 28;
  const x = Math.max(0, Math.floor((rect.x - pad) * scaleX));
  const y = Math.max(0, Math.floor((rect.y - pad) * scaleY));
  const width = Math.min(source.width - x, Math.ceil((rect.width + pad * 2) * scaleX));
  const height = Math.min(source.height - y, Math.ceil((rect.height + pad * 2) * scaleY));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  canvas.getContext("2d").drawImage(source, x, y, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function viewportSlice(source, scale) {
  const x = Math.max(0, Math.round(window.scrollX * scale));
  const y = Math.max(0, Math.round(window.scrollY * scale));
  const width = Math.min(source.width - x, Math.max(1, Math.round(window.innerWidth * scale)));
  const height = Math.min(source.height - y, Math.max(1, Math.round(window.innerHeight * scale)));
  if (x === 0 && y === 0 && width === source.width && height === source.height) return source;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(source, x, y, width, height, 0, 0, width, height);
  return canvas;
}

export async function captureSelectionShots(rects) {
  const { domToCanvas } = await import("modern-screenshot");
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const captured = await domToCanvas(document.documentElement, {
    scale,
    backgroundColor: "#0a0d14",
    filter: (node) => !(node instanceof Element && node.closest("[data-edit-chrome]")),
  });
  const canvas = viewportSlice(captured, scale);
  const ctx = canvas.getContext("2d");
  const scaleX = canvas.width / width;
  const scaleY = canvas.height / height;
  ctx.strokeStyle = "#22d3ee";
  ctx.lineWidth = Math.max(2, 2 * scaleX);
  (rects || []).forEach((rect) => {
    if (!rect || rect.width < 1 || rect.height < 1) return;
    ctx.strokeRect(rect.x * scaleX, rect.y * scaleY, rect.width * scaleX, rect.height * scaleY);
  });
  const viewport = canvasToEncoded(canvas);
  const united = unionRect(rects);
  const crop = united ? canvasToEncoded(cropCanvas(canvas, united, scaleX, scaleY)) : { encoded: "", mime: null };
  return {
    viewport_jpeg_base64: viewport.encoded || null,
    viewport_mime: viewport.encoded ? viewport.mime : null,
    crop_jpeg_base64: crop.encoded || null,
    crop_mime: crop.encoded ? crop.mime : null,
    screenshot_note: viewport.encoded ? null : "Screenshot capture returned an empty image.",
  };
}
